/**
 * Centralized auth/API failure diagnostics for local and staging debugging.
 *
 * Enable: runs automatically in Vite dev (import.meta.env.DEV), or set
 * VITE_AUTH_DIAGNOSTICS=true for production-like builds.
 *
 * Never logs full JWTs — only decoded claim summaries safe for the console.
 */

/** @returns {boolean} */
export function isAuthDiagnosticsEnabled() {
  try {
    if (import.meta.env?.DEV) return true;
    return import.meta.env?.VITE_AUTH_DIAGNOSTICS === 'true';
  } catch {
    return false;
  }
}

/**
 * Decode JWT payload (no signature verification — same as existing auth helpers).
 * @param {string|null|undefined} token - Raw JWT or "Bearer …"
 * @returns {object|null}
 */
export function decodeJwtPayloadSafe(token) {
  if (!token || typeof token !== 'string') return null;
  const raw = token.startsWith('Bearer ') ? token.slice(7).trim() : token;
  const parts = raw.split('.');
  if (parts.length !== 3) return { _parseError: 'not_a_jwt' };
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    const orgId = payload?.o?.id || payload?.org_id || null;
    const orgSlug = payload?.o?.slg || payload?.o?.slug || null;
    const now = Math.floor(Date.now() / 1000);
    const exp = typeof payload.exp === 'number' ? payload.exp : null;
    return {
      sub: payload.sub || null,
      iss: payload.iss || null,
      orgId,
      orgSlug,
      role: payload.o?.rol || payload.org_role || null,
      exp,
      expInSec: exp != null ? exp - now : null,
      azp: payload.azp || null,
      sid: payload.sid || null,
    };
  } catch (e) {
    return { _parseError: e?.message || 'decode_failed' };
  }
}

/**
 * @param {Request|string} input
 * @param {RequestInit} [init]
 * @returns {string|null}
 */
export function readAuthorizationHeader(input, init) {
  try {
    if (init?.headers) {
      const h = init.headers;
      if (typeof h.get === 'function') return h.get('Authorization') || h.get('authorization');
      return h.Authorization || h.authorization || null;
    }
    if (typeof input !== 'string' && input?.headers?.get) {
      return input.headers.get('Authorization') || input.headers.get('authorization');
    }
  } catch {
    // ignore
  }
  return null;
}

function shouldTraceApiUrl(url) {
  if (!url || typeof url !== 'string') return false;
  if (url.includes('clerk.accounts') || url.includes('clerk.com')) return false;
  return (
    url.includes('execute-api.') ||
    url.includes('amazonaws.com') ||
    url.includes('localhost') ||
    url.includes('127.0.0.1')
  );
}

let _installFetchDone = false;

/**
 * Wraps window.fetch once: on 401/403 to our APIs, logs structured diagnostics.
 * Call from index.jsx after env is available.
 */
export function installAuthFetchDiagnostics() {
  if (typeof window === 'undefined' || _installFetchDone) return;
  if (!isAuthDiagnosticsEnabled()) return;

  _installFetchDone = true;
  const orig = window.fetch.bind(window);

  window.fetch = async function authDiagFetch(input, init) {
    const response = await orig(input, init);

    const status = response.status;
    if (status !== 401 && status !== 403) {
      return response;
    }

    const url = typeof input === 'string' ? input : input?.url || '';
    if (!shouldTraceApiUrl(url)) {
      return response;
    }

    const method = (init && init.method) || (typeof input !== 'string' && input?.method) || 'GET';
    const authHeader = readAuthorizationHeader(input, init);
    const jwtSummary = decodeJwtPayloadSafe(authHeader);
    const authPresent = !!(authHeader && String(authHeader).length > 20);

    response
      .clone()
      .text()
      .then((text) => {
        const preview = (text || '').slice(0, 500);
        console.warn('[AuthDiag] HTTP ' + status + ' (fetch)', {
          method,
          url,
          authorizationHeaderPresent: authPresent,
          jwtClaims: jwtSummary,
          responseBodyPreview: preview || '(empty)',
        });
      })
      .catch(() => {
        console.warn('[AuthDiag] HTTP ' + status + ' (fetch)', {
          method,
          url,
          authorizationHeaderPresent: authPresent,
          jwtClaims: jwtSummary,
          responseBodyPreview: '(unreadable)',
        });
      });

    return response;
  };
}

/**
 * @param {string} label
 * @param {Record<string, unknown>} detail
 */
export function logAuthDiag(label, detail) {
  if (!isAuthDiagnosticsEnabled()) return;
  console.warn('[AuthDiag] ' + label, detail);
}
